import { Images, Pencil, Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { DashboardStats } from "../components/DashboardStats";
import { useNewTour } from "../components/NewTourModal";
import { ProjectEditModal } from "../components/ProjectEditModal";
import { TourCard } from "../components/TourCard";
import { Button, Card } from "../components/ui";
import { useT } from "../i18n";
import { usePageCrumbs } from "../lib/page-header";
import { byName, useProjects } from "../lib/projects";
import type { ProjectListItem } from "../../shared/api-types";

/**
 * Übersicht über alle Touren des Accounts. Objekte mit genau einer Tour stehen
 * flach im Raster — für sie wäre eine eigene Gruppe nur Rahmen ohne Inhalt.
 */
export function DashboardPage() {
  const { projects, error, reload } = useProjects();
  const [editProject, setEditProject] = useState<ProjectListItem | null>(null);
  const newTour = useNewTour();
  const t = useT();

  usePageCrumbs([{ label: t.app.navTours }]);

  const singles = (projects ?? []).filter((p) => p.tours.length === 1);
  const multis = byName((projects ?? []).filter((p) => p.tours.length > 1));
  // Objekte ohne Tour bleiben sichtbar — sonst wirkt das Löschen der letzten
  // Tour wie das Löschen des Objekts, und der leere Platz lädt zum Nachlegen ein.
  const empties = byName((projects ?? []).filter((p) => p.tours.length === 0));
  const total = (projects ?? []).reduce((n, p) => n + p.tours.length, 0);

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-8">
      <div className="mb-6 flex items-center justify-between gap-3">
        <h2 className="truncate text-lg font-semibold">{t.dashboard.title}</h2>
        <Button className="shrink-0" onClick={() => newTour()}>
          <Plus className="size-4" />
          {t.dashboard.newTour}
        </Button>
      </div>

      {error && <p className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</p>}

      {total > 0 && <DashboardStats />}

      {projects === null ? (
        <p className="text-sm text-zinc-400">{t.common.loading}</p>
      ) : projects.length === 0 ? (
        <Card>
          <div className="py-12 text-center">
            <Images className="mx-auto mb-3 size-8 text-zinc-300 dark:text-zinc-600" />
            <p className="font-medium">{t.dashboard.emptyTitle}</p>
            <p className="mb-5 mt-1 text-sm text-zinc-500 dark:text-zinc-400">
              {t.dashboard.emptyText}
            </p>
            <Button onClick={() => newTour()}>
              <Plus className="size-4" />
              {t.dashboard.firstTour}
            </Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-10">
          {singles.length > 0 && (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {singles.map((p) => (
                <TourCard
                  key={p.tours[0]!.id}
                  tour={p.tours[0]!}
                  eyebrow={p.name !== p.tours[0]!.name ? p.name : undefined}
                />
              ))}
            </div>
          )}
          {multis.map((p) => (
            <section key={p.id}>
              <ProjectHeading project={p} onEdit={() => setEditProject(p)} />
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {p.tours.map((tour) => (
                  <TourCard key={tour.id} tour={tour} />
                ))}
              </div>
            </section>
          ))}
          {empties.map((p) => (
            <section key={p.id}>
              <ProjectHeading project={p} onEdit={() => setEditProject(p)} />
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <button
                  onClick={() => newTour(p.id)}
                  className="flex aspect-[2/1] flex-col items-center justify-center gap-2 rounded-2xl border border-dashed border-zinc-300 text-sm text-zinc-500 transition-colors hover:border-accent hover:text-accent dark:border-zinc-700 dark:text-zinc-400"
                >
                  <Plus className="size-5" />
                  {t.dashboard.newTour}
                </button>
              </div>
            </section>
          ))}
        </div>
      )}

      <ProjectEditModal
        project={editProject}
        onClose={() => setEditProject(null)}
        onSaved={reload}
      />
    </main>
  );
}

/** Kopfzeile einer Projektgruppe: Name (führt zur Projektseite) und Stift. */
function ProjectHeading({ project, onEdit }: { project: ProjectListItem; onEdit: () => void }) {
  const t = useT();
  return (
    <div className="mb-3 flex items-center gap-1.5">
      <Link
        to={`/projects/${project.id}`}
        className="truncate text-sm font-medium text-zinc-500 transition-colors hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"
      >
        {project.name}
        {project.address && <span className="font-normal"> · {project.address}</span>}
      </Link>
      <button
        title={t.dashboard.editProjectTooltip}
        onClick={onEdit}
        className="shrink-0 rounded p-1 text-zinc-300 transition-colors hover:text-zinc-600 dark:text-zinc-600 dark:hover:text-zinc-300"
      >
        <Pencil className="size-3.5" />
      </button>
    </div>
  );
}
